/**
 * Admin Calendar Tab - Manage availability calendar
 */
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { CalendarDays, Plus, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAdmin } from "./AdminContext";
import { API } from "../../lib/api";

const AdminCalendarTab = () => {
  const { calendarEvents, googleCalendarStatus, loadData } = useAdmin();
  
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [newEvent, setNewEvent] = useState({ 
    title: "", start_date: "", end_date: "", event_type: "blocked", description: "" 
  });

  const handleSyncGoogleCalendar = async () => {
    setSyncingCalendar(true);
    try {
      await axios.post(`${API}/calendar/sync`, {}, { withCredentials: true });
      toast.success("Calendar synced!");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingCalendar(false);
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.start_date) {
      toast.error("Title and start date required");
      return;
    }
    try {
      await axios.post(`${API}/calendar/events`, newEvent, { withCredentials: true });
      toast.success("Event added");
      setNewEvent({ title: "", start_date: "", end_date: "", event_type: "blocked", description: "" });
      loadData();
    } catch { toast.error("Failed to add event"); }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`, { withCredentials: true });
      toast.success("Event deleted");
      loadData();
    } catch { toast.error("Delete failed"); }
  };

  const getEventTypeColor = (type) => {
    const colors = { 
      blocked: "bg-red-100 text-red-700", 
      booking: "bg-green-100 text-green-700", 
      holiday: "bg-purple-100 text-purple-700",
      maintenance: "bg-amber-100 text-amber-700"
    };
    return colors[type] || "bg-stone-100 text-stone-700";
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle><CalendarDays className="w-5 h-5 inline mr-2" />Calendar</CardTitle>
          <CardDescription>
            Manage availability and events
            {googleCalendarStatus?.connected && (
              <Badge className="ml-2 bg-green-100 text-green-700">Google Calendar Connected</Badge>
            )}
          </CardDescription>
        </div>
        <Button variant="outline" onClick={handleSyncGoogleCalendar} disabled={syncingCalendar}>
          <RefreshCw className={`w-4 h-4 mr-2 ${syncingCalendar ? 'animate-spin' : ''}`} />
          {syncingCalendar ? "Syncing..." : "Sync Google Calendar"}
        </Button>
      </CardHeader>
      <CardContent>
        {/* Add Event Form */}
        <div className="mb-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <h3 className="font-semibold mb-4">Add Calendar Event</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Title *</Label><Input value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} placeholder="e.g., Holiday closure" /></div>
            <div><Label>Event Type</Label>
              <Select value={newEvent.event_type} onValueChange={v => setNewEvent({...newEvent, event_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="booking">Booking</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Start Date *</Label><Input type="date" value={newEvent.start_date} onChange={e => setNewEvent({...newEvent, start_date: e.target.value})} /></div>
            <div><Label>End Date</Label><Input type="date" value={newEvent.end_date} onChange={e => setNewEvent({...newEvent, end_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Description</Label><Textarea value={newEvent.description} onChange={e => setNewEvent({...newEvent, description: e.target.value})} rows={2} /></div>
          </div>
          <Button onClick={handleAddEvent} className="mt-4 bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" /> Add Event
          </Button>
        </div>

        {/* Events List */}
        {calendarEvents.length === 0 ? (
          <p className="text-center text-stone-500 py-8">No calendar events</p>
        ) : (
          <div className="space-y-3">
            {calendarEvents.map(event => (
              <div key={event.event_id} className="p-4 border rounded-lg flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{event.title}</p>
                    <Badge className={getEventTypeColor(event.event_type)}>{event.event_type}</Badge>
                  </div>
                  <p className="text-sm text-stone-500">
                    {event.start_date?.split("T")[0]}
                    {event.end_date && ` - ${event.end_date?.split("T")[0]}`}
                  </p>
                  {event.description && <p className="text-sm text-stone-600 mt-1">{event.description}</p>}
                </div>
                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteEvent(event.event_id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminCalendarTab;
